import api from './api'

// Flag to use mock data (set to true for development without backend)
const USE_MOCK_DATA = true

// Mock user data
const mockUsers = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john@example.com',
    password: 'password123' // In real app, this would be hashed
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane@example.com',
    password: 'password123'
  }
]

// Mock JWT token
const generateMockToken = (user) => {
  return `mock-jwt-token-${user.id}-${Date.now()}`
}

export const authService = {
  // Login user
  login: async (credentials) => {
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const user = mockUsers.find(u => 
        u.email === credentials.email && u.password === credentials.password
      )
      
      if (!user) {
        throw new Error('Invalid email or password')
      }
      
      const token = generateMockToken(user)
      const { password, ...userWithoutPassword } = user
      
      return {
        token,
        user: userWithoutPassword
      }
    }
    
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  // Register user
  register: async (userData) => {
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1200))
      
      // Check if user already exists
      const existingUser = mockUsers.find(u => u.email === userData.email)
      if (existingUser) {
        throw new Error('User with this email already exists')
      }
      
      // Create new user
      const newUser = {
        id: mockUsers.length + 1,
        name: userData.name,
        email: userData.email,
        password: userData.password
      }
      
      mockUsers.push(newUser)
      
      const token = generateMockToken(newUser)
      const { password, ...userWithoutPassword } = newUser
      
      return {
        token,
        user: userWithoutPassword
      }
    }
    
    const response = await api.post('/auth/register', userData)
    return response.data
  },

  // Get current user profile
  getProfile: async () => {
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // In a real app, you'd decode the JWT token to get user info
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No token found')
      }
      
      // Mock user data based on token
      const userId = token.includes('mock-jwt-token-1') ? 1 : 2
      const user = mockUsers.find(u => u.id === userId)
      
      if (!user) {
        throw new Error('User not found')
      }
      
      const { password, ...userWithoutPassword } = user
      return userWithoutPassword
    }
    
    const response = await api.get('/auth/profile')
    return response.data
  },

  // Update user profile
  updateProfile: async (userData) => {
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800))
      
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No token found')
      }
      
      const userId = token.includes('mock-jwt-token-1') ? 1 : 2
      const userIndex = mockUsers.findIndex(u => u.id === userId)
      
      if (userIndex === -1) {
        throw new Error('User not found')
      }
      
      // Update user data
      mockUsers[userIndex] = { ...mockUsers[userIndex], ...userData }
      const { password, ...userWithoutPassword } = mockUsers[userIndex]
      
      return userWithoutPassword
    }
    
    const response = await api.put('/auth/profile', userData)
    return response.data
  },

  // Refresh token
  refreshToken: async () => {
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300))
      
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No token found')
      }
      
      // Generate new token
      const userId = token.includes('mock-jwt-token-1') ? 1 : 2
      const user = mockUsers.find(u => u.id === userId)
      
      if (!user) {
        throw new Error('User not found')
      }
      
      const newToken = generateMockToken(user)
      return { token: newToken }
    }
    
    const response = await api.post('/auth/refresh')
    return response.data
  },
}